import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { COUNTRIES } from "@/lib/countries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const money = (v, c) => `${c} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const FAQS = [
  ["Why is my small consignment priced the same as a heavier one?", "Minimum charge. Light loads are almost always priced at the minimum, so the per-kg rate you negotiated does not apply — negotiate the minimum instead."],
  ["What is volumetric weight?", "Air and parcel freight charge on actual or volumetric weight, whichever is greater. A light, bulky crate is priced as though it were heavy."],
  ["Are these real prices?", "They come from live rate cards published by carriers on Unity & Utility. Sign in to book, insure and pay for the load."],
];

export default function FreightCalculator() {
  const [form, setForm] = useState({ pickup_country: "US", dropoff_country: "US", weight_kg: 250, distance_km: 120 });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useSeo({
    title: "Free freight quote calculator — compare carriers on landed cost | Unity & Utility",
    description: "Work out what a shipment really costs: base rate, weight, distance, fuel surcharge, customs and transit days, compared across every carrier on the lane. Free, no sign-in.",
    path: "/tools/freight-calculator",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        breadcrumbs([["Home", "/"], ["Freight calculator", "/tools/freight-calculator"]]),
        { "@type": "FAQPage", mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
      ],
    },
  });

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public-site/freight-estimate", {
        pickup_country: form.pickup_country, dropoff_country: form.dropoff_country,
        weight_kg: Number(form.weight_kg) || 0, distance_km: Number(form.distance_km) || 0,
      });
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid="freight-calculator-page">
        <Crumbs items={[["Home", "/"], ["Freight calculator", null]]} />
        <p className="overline text-[#002FA7]">Free tool</p>
        <h1 className="font-display mt-3 text-4xl font-bold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl">
          Freight quote calculator
        </h1>
        <p className="mt-4 text-base leading-relaxed text-slate-600">
          Put in the lane, the weight and the distance and see what each carrier would charge —
          base rate, fuel surcharge, customs and transit days, totalled so you compare landed cost
          rather than the headline.
        </p>

        <div className="mt-8 grid gap-3 border border-border bg-white p-5 sm:grid-cols-2">
          <div>
            <Label>Collecting from</Label>
            <Select value={form.pickup_country} onValueChange={(v) => setForm({ ...form, pickup_country: v })}>
              <SelectTrigger data-testid="freight-pickup-country" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Delivering to</Label>
            <Select value={form.dropoff_country} onValueChange={(v) => setForm({ ...form, dropoff_country: v })}>
              <SelectTrigger data-testid="freight-dropoff-country" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Weight (kg)</Label>
            <Input data-testid="freight-weight-input" type="number" min="0" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="rounded-none border-slate-300" />
          </div>
          <div>
            <Label>Distance (km)</Label>
            <Input data-testid="freight-distance-input" type="number" min="0" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} className="rounded-none border-slate-300" />
          </div>
          <div className="sm:col-span-2">
            <Button data-testid="freight-calculate-button" onClick={run} disabled={busy} className="w-full sm:w-auto">
              <Calculator size={16} className="mr-1.5" /> {busy ? "Working it out…" : "Compare carriers"}
            </Button>
          </div>
        </div>

        {result && (
          <div className="mt-6" data-testid="freight-results">
            {result.options.length === 0 ? (
              <p data-testid="freight-no-options" className="border border-border bg-white p-5 text-sm text-slate-600">
                No carrier on the platform covers that lane yet. Try a different pair of countries — or
                list your own rate card once you sign in.
              </p>
            ) : (
              <>
                <p className="text-sm text-slate-600" data-testid="freight-summary">
                  {result.options.length} {result.scope} service{result.options.length > 1 ? "s" : ""} on this lane
                  {result.spread > 0 && <> · cheapest to dearest differs by <strong>{money(result.spread, result.options[0].currency)}</strong></>}
                </p>
                <div className="mt-3 space-y-2">
                  {result.options.map((o, i) => (
                    <div key={`${o.service}-${i}`} data-testid={`freight-option-${i}`} className={`border bg-white p-4 ${i === 0 ? "border-[#002FA7]" : "border-border"}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold text-[#0F172A]">
                            {o.service}
                            {i === 0 && <span className="ml-2 border border-[#A7E3CE] bg-[#E6F7F1] px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-[#0B7A5B]">Cheapest</span>}
                          </div>
                          <div className="text-xs text-slate-500">{o.carrier_name} · {o.transit_days} day transit</div>
                        </div>
                        <div className="font-display text-lg font-bold text-[#0F172A]" data-testid={`freight-total-${i}`}>{money(o.total, o.currency)}</div>
                      </div>
                      <dl className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-5">
                        {[["Base", o.base_charge], ["Weight", o.weight_charge], ["Distance", o.distance_charge], ["Fuel", o.fuel_surcharge], ["Customs", o.customs_fee]].map(([l, v]) => (
                          <div key={l}>
                            <dt className="overline text-[0.6rem] text-slate-400">{l}</dt>
                            <dd className="text-slate-700">{money(v, o.currency)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-slate-500">{result.note}</p>
                <Link to="/register" data-testid="freight-cta-register" className="mt-4 inline-flex items-center gap-2 bg-[#002FA7] px-5 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#00248A]">
                  Book this load <ArrowRight size={15} />
                </Link>
              </>
            )}
          </div>
        )}

        <h2 className="mt-12 text-base font-semibold text-[#0F172A] md:text-lg">Questions people ask</h2>
        <dl className="mt-3 space-y-4" data-testid="freight-faqs">
          {FAQS.map(([q, a]) => (
            <div key={q} className="border border-border bg-white p-4">
              <dt className="text-sm font-semibold text-[#0F172A]">{q}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-slate-600">{a}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-10 border-t border-border pt-5">
          <p className="text-xs text-slate-500">Share this calculator</p>
          <div className="mt-2"><ShareBar url={`${window.location.origin}/tools/freight-calculator`} text="Free freight quote calculator" testid="freight-share" /></div>
        </div>

        <p className="mt-8 text-sm text-slate-600">
          Related: <Link to="/learn/compare-freight-quotes" className="font-semibold text-[#002FA7] hover:underline">how to compare freight quotes</Link>
          {" · "}<Link to="/solutions/freight" className="font-semibold text-[#002FA7] hover:underline">shipping in the platform</Link>
        </p>
      </div>
    </PublicShell>
  );
}
